import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { TransformComponent, TransformWrapper } from 'react-zoom-pan-pinch';
import { useAppSelector } from 'hooks';
import { InspectionImages } from 'models/response/InspectionResponse';
import { usePatchCarImageMutation, useRemoveCarImageMutation } from 'services/modules/inspection';
import { Button, Image, Spinner, TakeImage } from 'ui';
import DefaultImage from 'ui/DefaultImage';
import { fileDocumentType, mediaType } from 'ui/TakeImage';

import { FileSvg } from '../../svg';
import { Modal } from '../../ui/Modal';
import { fileUrl, imageUrl, videoUrl } from '../../utils/imageUrl';
import ImageEditor from '../CarPictures/Components/ImageEditor';
import { ErrorBodyWrapper, ErrorButtons } from '../CarSegments/CarSegmentsElements';

import {
  BottomContainer,
  Container,
  ContentContainer,
  IconButton,
  ImageContainer,
  Label,
  TopContainer,
} from './ImageViewerElements';

export const ImageViewer = () => {
  const navigate = useNavigate();
  const params = useParams();
  const [searchParams] = useSearchParams();
  const { t } = useTranslation();

  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const toggleShowConfirmModal = () => setShowConfirmModal((state) => !state);
  const { currentImage } = useAppSelector((state) => state.inspection);
  const [imageSelected, setImageSelected] = useState<InspectionImages | null>(null);
  const [isOpenTakeImage, onToggleIsOpenTakeImage] = useState(false);
  const [isOpenEditImage, onToggleIsOpenEditImage] = useState(false);
  const type = searchParams.get('type') || '';
  const [removeCarImage, { isLoading: isLoadingRemove, isSuccess: isSuccessRemove }] =
    useRemoveCarImageMutation();
  const [updateImage, { data: dataImage, isLoading: isLoadingImage, isSuccess: isSuccessImage }] =
    usePatchCarImageMutation();
  const isLoading = isLoadingImage || isLoadingRemove;

  useEffect(() => {
    setImageSelected(currentImage);
  }, [currentImage]);

  const onChooseImage = (file: File) => {
    const slug = params.slug;
    const template_slug = currentImage?.slug;
    if (slug && template_slug) {
      const body = new FormData();
      body.append('image', file, file.name);
      body.append('template_slug', template_slug);
      updateImage({ slug, body });
    }
  };

  useEffect(() => {
    if (isSuccessImage && dataImage && currentImage) {
      setImageSelected({ ...currentImage, image: dataImage.uploaded_image });
    }
  }, [dataImage, isSuccessImage]);

  const onClickRemove = () => {
    if (isLoading) {
      return;
    }
    const slug = params.slug;
    const file_uuid = imageSelected?.image?.uuid;
    if (file_uuid && slug) {
      removeCarImage({ slug, file_uuid });
    }
  };

  useEffect(() => {
    if (isSuccessRemove && currentImage) {
      setImageSelected({ ...currentImage, image: null });
      toggleShowConfirmModal();
    }
  }, [isSuccessRemove]);

  const handleUploadImage = () => {
    if (isLoading) {
      return;
    }
    onToggleIsOpenTakeImage(true);
    setTimeout(() => {
      onToggleIsOpenTakeImage(false);
    }, 0);
  };

  return (
    <Container>
      <TopContainer>
        <div className="tw-flex tw-items-center">
          <IconButton
            onClick={() => {
              onToggleIsOpenEditImage(true);
            }}
            className="tw-text-gray0 !tw-text-[16px] !tw-bg-gray400"
          >
            {t('image_picker.customize')}
          </IconButton>
        </div>
        <div className="tw-flex tw-gap-2 tw-items-center">
          <IconButton
            className={'icon-u_trash-alt'}
            disabled={isLoading}
            onClick={toggleShowConfirmModal}
          />
          <IconButton
            className={'icon-Left-icon'}
            disabled={isLoading}
            onClick={() => handleUploadImage()}
          />
          <IconButton
            className={'icon-Left-icon34'}
            disabled={isLoading}
            onClick={() => navigate(-1)}
          />
        </div>
      </TopContainer>
      <ContentContainer>
        <ImageContainer>
          {!isLoading &&
            (imageSelected && imageSelected.image ? (
              <TransformWrapper>
                <TransformComponent>
                  {imageSelected.image.type.includes('image') ? (
                    <Image src={imageSelected.image.url} height={900} width={1200} />
                  ) : imageSelected.image.type.includes('video') ? (
                    // eslint-disable-next-line jsx-a11y/media-has-caption
                    <video src={videoUrl(imageSelected.image.url)} controls width="100%">
                      Your browser does not support the video tag.
                    </video>
                  ) : (
                    <button onClick={() => window.open(fileUrl(imageSelected?.image?.url))}>
                      <div className={'tw-flex tw-flex-col tw-items-center tw-gap-1'}>
                        <FileSvg className={'tw-w-[80px] tw-h-[80px] tw-fill-primaryGreen500'} />
                        <p
                          className={'tw-text-primaryGreen500 tw-uppercase tw-text-lg tw-font-bold'}
                        >
                          {imageSelected.image.url.split('.')?.[1]}
                        </p>
                      </div>
                    </button>
                  )}
                </TransformComponent>
              </TransformWrapper>
            ) : (
              <DefaultImage />
            ))}
          <Spinner isLoading={isLoading} />
        </ImageContainer>
        {currentImage?.title && (
          <BottomContainer>
            <Label>{currentImage?.title}</Label>
            <Label>{params.index?.replace('-', '/')}</Label>
          </BottomContainer>
        )}
      </ContentContainer>
      <TakeImage
        onChooseImage={onChooseImage}
        isRotate={type !== 'documents'}
        allowedTypes={(type !== 'documents' ? mediaType : fileDocumentType) || undefined}
        isOpenTakeImage={isOpenTakeImage}
      />

      {imageSelected?.image?.url && isOpenEditImage && (
        <ImageEditor
          image={imageUrl({ src: imageSelected.image.url, height: 900, width: 1200 })}
          onCloseModal={() => onToggleIsOpenEditImage(false)}
          onEditedImage={onChooseImage}
        />
      )}
      <Modal
        title={t('inspection.remove_image')}
        onCloseModal={toggleShowConfirmModal}
        isShow={showConfirmModal}
      >
        <ErrorBodyWrapper>
          <p>{t('inspection.are_you_sure_you_want_remove')}</p>
        </ErrorBodyWrapper>
        <ErrorButtons>
          <Button fullWidth onClick={onClickRemove} isLoading={isLoadingRemove}>
            {t('car_inspection.confirm')}
          </Button>
          <Button fullWidth color="red" onClick={toggleShowConfirmModal}>
            {t('car_inspection.cancel')}
          </Button>
        </ErrorButtons>
      </Modal>
    </Container>
  );
};
